import { SessionProvider } from "next-auth/react";
import "../styles/tailwind.css";
import type { ReactElement, ReactNode } from "react";
// import { Toaster } from "react-hot-toast";
import type { NextPage } from "next";
import { type Session } from "next-auth";
import type { AppProps, AppType } from "next/app";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import Script from "next/script";
// import Chatbot from "@/common/Chatbot/index";
// import { Analytics } from "@vercel/analytics/react";
import WelcomeModal from "@/components/welcomeModal";
import apiClient from "@/utils/apiClient";
import { SpeedInsights } from "@vercel/speed-insights/next"
import dynamic from "next/dynamic";
import Chatbot from "@/common/Chatbot";

const NEXT_PUBLIC_GA4_ID = "G-MJ64LCY1PL";

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement, props?: any) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const MyApp: AppType<{ session: Session | null }> = ({
  Component,
  pageProps: { session, ...pageProps },
}: AppPropsWithLayout) => {
  const router = useRouter();

  const [showAll, setShowAll] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const fetchNotifications = async () => {
    if (notifications.length > 0) return;
    try {
      const res = await apiClient.get(`${apiClient.URLS.notifications}/1`);
      if (res.status === 200) {
        setNotifications(res.body);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const setShowAllMemo = useCallback((val: boolean) => setShowAll(val), []);
  const setNotificationsMemo = useCallback(
    (val: Notification[]) => setNotifications(val),
    []
  );
  const layoutProps = useMemo(
    () => ({
      showAll,
      setShowAll: setShowAllMemo,
      notifications,
      setNotifications: setNotificationsMemo,
    }),
    [showAll, notifications, setShowAllMemo, setNotificationsMemo]
  );

  useEffect(() => {
    setTimeout(fetchNotifications, 3000);
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (typeof (window as any).gtag === "function") {
        (window as any).gtag("config", NEXT_PUBLIC_GA4_ID, { page_path: url });
      } else {
        console.error("gtag is not a function");
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  const Chatbot = dynamic(() => import("@/common/Chatbot/index"), {
    ssr: false,
  });
  const Toaster = dynamic(
    () => import("react-hot-toast").then((mod) => mod.Toaster),
    { ssr: false }
  );
  const Analytics = dynamic(
    () => import("@vercel/analytics/react").then((mod) => mod.Analytics),
    { ssr: false }
  );



  const getLayout = Component.getLayout
    ? (page: ReactElement) =>
      Component.getLayout!(page, {
        layoutProps
      })
    : (page: ReactElement) => page;

  return (
    <>
      <>
        <Script
          src={`https://www.googletagmanager.com/gtag/js?id=${NEXT_PUBLIC_GA4_ID}`}
        />
        <Script
          id="google-analytics"
          dangerouslySetInnerHTML={{
            __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${NEXT_PUBLIC_GA4_ID}', {
              page_path: window.location.pathname,
            });
          `,
          }}
        />
        <Script
          id="adsense-script"
          strategy="lazyOnload"
          async
          src={process.env.NEXT_PUBLIC_GOOGLE_AD_SRC}
          crossOrigin="anonymous"
        />
      </>
      <SessionProvider session={session as Session}>
        <div>{getLayout(<Component {...pageProps} />)}</div>
        <Chatbot />
        <Analytics />
        <Toaster position="top-right" reverseOrder={false} />
      </SessionProvider>
    </>
  );
};

export default MyApp;
